import React from 'react'
import { Plus } from 'lucide-react'
import CurrencyDisplay from '../shared/CurrencyDisplay'

interface Product {
  id: string | number
  name?: string
  name_en?: string
  price?: number
  image?: string
}

interface ProductsGridSectionProps {
  products: Product[]
  isLoading: boolean
  currency: string
  onProductSelect: (product: Product) => void
  scrollRef?: React.Ref<HTMLDivElement>
}

const ProductsGridSection: React.FC<ProductsGridSectionProps> = ({
  products,
  isLoading,
  onProductSelect,
  scrollRef
}) => {
  if (isLoading) {
    return (
      <div className="flex-1 overflow-y-auto hide-scrollbar px-4 pb-20">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-4">
          {Array.from({ length: 12 }).map((_, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-2 sm:p-3 animate-pulse">
              <div className="aspect-[197/136] bg-gray-200 rounded-xl mb-2 sm:mb-3" />
              <div className="px-1 sm:px-2 pb-1 sm:pb-2 space-y-2">
                <div className="h-3 sm:h-4 bg-gray-200 rounded" />
                <div className="h-4 sm:h-5 bg-gray-200 rounded w-2/3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (products.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto hide-scrollbar px-4 pb-20">
        <div className="flex items-center justify-center h-full">
          <span className="text-sm text-[#525866]">لا توجد منتجات</span>
        </div>
      </div>
    )
  }

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto hide-scrollbar px-4 pb-20">
      <div className="grid grid-cols-1   sm:grid-cols-1  md:grid-cols-3   lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
        {products.map((product) => (
          <div
            key={product.id}
            className="bg-white rounded-2xl p-2 sm:p-3 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onProductSelect(product)}
          >
            <img 
              className="w-full aspect-[197/136] object-contain rounded-xl mb-2 sm:mb-3" 
              src={product.image || 'https://placehold.co/197x136'} 
              alt={product.name || 'Product'}
              onError={(e) => {
                e.currentTarget.src = 'https://placehold.co/197x136'
              }}
            />
            <div className="px-1 sm:px-2 pb-1 sm:pb-2 flex items-end gap-1.5 sm:gap-2">
              <div className="flex-1 flex flex-col gap-0.5 sm:gap-1 min-w-0">
                <div className="text-xs sm:text-sm md:text-base font-medium truncate leading-tight">
                  {product.name || 'Product'}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs sm:text-sm md:text-base font-semibold whitespace-nowrap">{product.price || 0}</span>
                  <CurrencyDisplay className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                </div>
              </div>
              <button 
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-[10px] border border-[#E1E4EA] flex items-center justify-center shrink-0 hover:bg-gray-50 transition-colors"
                onClick={(e) => {
                  e.stopPropagation()
                  onProductSelect(product)
                }}
              >
                <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#525866]" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ProductsGridSection

